import { supabase } from './supabase';
import { enqueue, flushSoon, pendingIds } from './outbox';
import { fetchAllRows } from './storage';

const LS = (uid) => `ac_text_proj_${uid}`;

export function genTextId() {
  try { return crypto.randomUUID(); }
  catch { return `tp_${Date.now()}_${Math.random().toString(36).slice(2)}`; }
}

export function createTextProject(overrides = {}) {
  const now = new Date().toISOString();
  return {
    id:              genTextId(),
    name:            'New Text',
    content:         '',
    folder:          '',
    src_lang:        'en',
    tgt_lang:        'ru',
    current_page:    0,
    words_per_page:  300,
    linked_audio_id: null,
    youtube_id:      null,
    yt_segments:     [],
    created_at:      now,
    updated_at:      now,
    ...overrides,
  };
}

export function loadLocalTextProjects(userId) {
  try { return JSON.parse(localStorage.getItem(LS(userId)) || '[]'); }
  catch { return []; }
}

function _saveAllLocal(userId, list) {
  try { localStorage.setItem(LS(userId), JSON.stringify(list)); } catch {}
}

export function saveTextProjectLocal(userId, proj) {
  const list = loadLocalTextProjects(userId);
  const i = list.findIndex(p => p.id === proj.id);
  if (i >= 0) list[i] = proj; else list.unshift(proj);
  _saveAllLocal(userId, list);
}

export function deleteTextProjectLocal(userId, projId) {
  _saveAllLocal(userId, loadLocalTextProjects(userId).filter(p => p.id !== projId));
}

const toRow = (userId, proj, updatedAt) => ({
  id:              proj.id,
  user_id:         userId,
  name:            proj.name            || 'Untitled',
  content:         proj.content         || '',
  folder:          proj.folder          || '',
  src_lang:        proj.src_lang        || 'en',
  tgt_lang:        proj.tgt_lang        || 'ru',
  current_page:    proj.current_page    ?? 0,
  words_per_page:  proj.words_per_page  ?? 300,
  linked_audio_id: proj.linked_audio_id || null,
  youtube_id:      proj.youtube_id      || null,
  yt_segments:     proj.yt_segments     || [],
  created_at:      proj.created_at      || updatedAt,
  updated_at:      updatedAt,
});

const META = 'id,name,folder,src_lang,tgt_lang,current_page,words_per_page,linked_audio_id,youtube_id,yt_segments,created_at,updated_at';

// Забрать тексты с сервера. Сначала только описания (без самих текстов), потом
// содержимое — лишь для новых и изменённых: раньше при каждом открытии сайта
// скачивались все тексты целиком. Локальные правки, ещё не отправленные на
// сервер, сохраняются. Бросает ошибку, если сервер недоступен.
export async function pullTextProjects(userId) {
  const meta = await fetchAllRows(() => supabase.from('text_projects').select(META)
    .eq('user_id', userId).order('updated_at', { ascending: false }).order('id'));
  const local = loadLocalTextProjects(userId);
  const localById = new Map(local.map(p => [p.id, p]));
  const pending = pendingIds().texts;

  const need = meta
    .filter(m => !pending.has(m.id))
    .filter(m => { const l = localById.get(m.id); return !l || typeof l.content !== 'string' || Date.parse(l.updated_at) !== Date.parse(m.updated_at); })
    .map(m => m.id);
  const contents = new Map();
  for (let i = 0; i < need.length; i += 20) {
    const { data, error } = await supabase.from('text_projects').select('id,content').in('id', need.slice(i, i + 20));
    if (error) throw error;
    (data || []).forEach(r => contents.set(r.id, r.content || ''));
  }

  const serverIds = new Set(meta.map(m => m.id));
  const merged = meta.map(m => {
    const l = localById.get(m.id);
    if (l && pending.has(m.id)) return { ...l, _synced: true }; // правка ещё в пути — оставляем свою
    return { ...m, content: contents.has(m.id) ? contents.get(m.id) : (l?.content ?? ''), _synced: true };
  });
  for (const l of local) {
    if (serverIds.has(l.id)) continue;
    if (pending.has(l.id)) { merged.unshift(l); continue; }
    if (!l._synced) {
      // Текст так и не дошёл до сервера (старая версия теряла ошибки записи) — дошлём.
      enqueue('text.upsert', 'text:' + l.id, toRow(userId, l, l.updated_at || new Date().toISOString()));
      merged.unshift(l);
    }
    // _synced и нет на сервере — удалён на другом устройстве.
  }
  _saveAllLocal(userId, merged);
  flushSoon();
  return merged;
}

export async function loadTextProjects(userId) {
  try { return await pullTextProjects(userId); }
  catch (e) { console.warn('[textProjects] pull:', e?.message); }
  return loadLocalTextProjects(userId);
}

const SAME_FIELDS = ['name', 'content', 'folder', 'src_lang', 'tgt_lang', 'words_per_page', 'linked_audio_id', 'youtube_id'];
const same = (a, b) => (a ?? null) === (b ?? null);

// Сохранить текст: на устройство сразу, на сервер — через очередь отправки.
// Если изменилась только страница чтения — шлём одно поле, а не весь текст
// (раньше каждый перелистнутый лист заново заливал текст целиком).
export async function upsertTextProject(userId, proj) {
  const prev = loadLocalTextProjects(userId).find(p => p.id === proj.id);
  const onlyProgress = prev
    && SAME_FIELDS.every(f => same(prev[f], proj[f]))
    && JSON.stringify(prev.yt_segments || []) === JSON.stringify(proj.yt_segments || []);
  if (onlyProgress) {
    const updated = { ...prev, ...proj, updated_at: prev.updated_at };
    saveTextProjectLocal(userId, updated);
    if (!same(prev.current_page, proj.current_page) && userId) {
      enqueue('text.patch', 'text:' + proj.id, { id: proj.id, fields: { current_page: proj.current_page ?? 0 } });
      flushSoon(4000);
    }
    return updated;
  }
  const now = new Date().toISOString();
  const updated = { ...proj, updated_at: now, _synced: prev?._synced };
  saveTextProjectLocal(userId, updated);
  if (userId) {
    enqueue('text.upsert', 'text:' + proj.id, toRow(userId, proj, now));
    flushSoon();
  }
  return updated;
}

export async function deleteTextProject(userId, projId) {
  deleteTextProjectLocal(userId, projId);
  enqueue('text.delete', 'text:' + projId, { id: projId });
  flushSoon(300);
}
